import { checksSentence, checksSentenceParts, type Checks } from "@/lib/upload-report";

export type SummaryVersion = {
  id: string;
  n: number;
  uploadedAt: string;
  archivedAt?: string | null;
  gen?: number;
  pageCount?: number;
  uploadedBy?: { id: string; name: string } | null;
  checks?: Checks;
};

export type SummaryIssue = {
  status?: string;
  publishDate?: string | null;
  documents: { id: string; versions: SummaryVersion[]; processingUploads?: number }[];
};

export type CurrentVersion = SummaryVersion & { documentId: string };

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const STATUS_LABELS: Record<string, string> = { draft: "Draft", in_review: "In review", at_printer: "At the printer", printed: "Printed" };

/** The newest non-archived proof across the issue's documents. */
export function currentIssueVersion(issue: SummaryIssue | null | undefined): CurrentVersion | null {
  if (!issue) return null;
  const versions = issue.documents.flatMap((document) => document.versions.map((version) => ({ ...version, documentId: document.id })));
  return versions
    .filter((version) => version.archivedAt === null || version.archivedAt === undefined)
    .reduce<CurrentVersion | undefined>((latest, version) => (
      !latest || version.uploadedAt > latest.uploadedAt || (version.uploadedAt === latest.uploadedAt && version.n > latest.n) ? version : latest
    ), undefined) ?? null;
}

/** Calendar dates stay calendar dates for every reader, so these getters are UTC. */
export function pressDateWords(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

/** The state phrase used before the press-date sentence. */
export function issueStateLine(status: string | undefined): string {
  if (!status) return "";
  return STATUS_LABELS[status] ?? `${status.charAt(0).toUpperCase()}${status.slice(1).replace(/_/g, " ")}`;
}

/** The issue home keeps the running line compact; every other state shares Review's wording. */
export function issueChecksSentence(version: SummaryVersion | null | undefined): string | null {
  const sentence = checksSentence(version?.checks, version?.uploadedBy?.name);
  if (!sentence || version?.checks?.status !== "running") return sentence;
  return checksSentenceParts(sentence)[1];
}
